import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { 
  Mail, 
  MessageSquare, 
  Users, 
  Send, 
  Eye, 
  Calendar, 
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Download,
  Upload,
  Filter,
  Search,
  X,
  CheckCircle,
  AlertTriangle,
  Clock,
  Save
} from "lucide-react";

// Firebase imports
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

const CampaignManager = ({ user, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('campaigns');
  
  // Validation des props
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Erreur : utilisateur non défini</p>
          <Button onClick={onClose} className="mt-4">Retour</Button>
        </div>
      </div>
    );
  }
  
  // États pour les campagnes
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  // États pour les abonnés
  const [subscribers, setSubscribers] = useState([]);
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  
  // États pour les templates
  const [templates, setTemplates] = useState([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showPreviewTemplate, setShowPreviewTemplate] = useState(false);
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // États pour les statistiques
  const [stats, setStats] = useState({
    totalSubscribers: 0,
    totalCampaigns: 0,
    totalSent: 0,
    avgOpenRate: 0,
    avgClickRate: 0
  });

  // État pour les formulaires
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    subject: '',
    content: '',
    type: 'email', // email ou sms
    template: '',
    scheduledDate: '',
    targetAudience: 'all', // all, chefs, clients, custom
    status: 'draft'
  });

  const [subscriberForm, setSubscriberForm] = useState({
    email: '',
    name: '',
    phone: '',
    type: 'client', // client, chef, admin
    newsletter: true,
    sms: false
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    content: '',
    type: 'email'
  });

  // États pour les alertes
  const [showAlert, setShowAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    title: '',
    message: '',
    type: 'success'
  });

  useEffect(() => {
    loadCampaignData();
  }, []);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      
      console.log('📊 Chargement des données de campagne...');
      
      // Toujours charger d'abord les données de démo
      loadDemoData();
      
      // Ensuite essayer de charger depuis Firestore et merger si possible
      try {
        // Charger les campagnes depuis Firestore
        const campaignsSnapshot = await getDocs(collection(db, 'campaigns'));
        if (campaignsSnapshot.docs.length > 0) {
          const campaignsData = campaignsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setCampaigns(prev => [...prev, ...campaignsData]);
          console.log(`✅ ${campaignsData.length} campagnes chargées depuis Firestore`);
        }

        // Charger les abonnés depuis Firestore
        const subscribersSnapshot = await getDocs(collection(db, 'newsletter-subscribers'));
        if (subscribersSnapshot.docs.length > 0) {
          const subscribersData = subscribersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setSubscribers(prev => [...prev, ...subscribersData]);
          console.log(`✅ ${subscribersData.length} abonnés chargés depuis Firestore`);
        }

        // Charger les templates depuis Firestore
        const templatesSnapshot = await getDocs(collection(db, 'email-templates'));
        if (templatesSnapshot.docs.length > 0) {
          const templatesData = templatesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setTemplates(prev => [...prev, ...templatesData]);
          console.log(`✅ ${templatesData.length} templates chargés depuis Firestore`);
        }
      } catch (firestoreError) {
        console.log('⚠️ Erreur Firestore, utilisation des données de démo uniquement:', firestoreError.message);
      }
      
    } catch (error) {
      console.error('Erreur critique lors du chargement:', error);
      // En cas d'erreur critique, s'assurer qu'on a au moins les données de démo
      loadDemoData();
    } finally {
      setLoading(false);
    }
  };

  const loadDemoData = () => {
    const demoCampaigns = [
      {
        id: '1',
        name: 'Newsletter Octobre 2024',
        subject: 'Découvrez nos nouveaux ateliers !',
        type: 'email',
        status: 'sent',
        sentCount: 150,
        openCount: 89,
        clickCount: 23,
        createdAt: new Date('2024-10-01')
      },
      {
        id: '2',
        name: 'Rappel SMS Ateliers',
        subject: 'Votre atelier commence dans 2h !',
        type: 'sms',
        status: 'sent',
        sentCount: 45,
        createdAt: new Date('2024-10-15')
      }
    ];

    const demoSubscribers = [
      {
        id: '1',
        email: 'marie.dubois@example.com',
        name: 'Marie Dubois',
        type: 'client',
        newsletter: true,
        subscribedAt: new Date('2024-09-15')
      },
      {
        id: '2',
        email: 'chef.antoine@example.com',
        name: 'Chef Antoine',
        type: 'chef',
        newsletter: true,
        subscribedAt: new Date('2024-09-20')
      }
    ];

    const demoTemplates = [
      {
        id: 'template-1',
        name: 'Newsletter Basique',
        subject: 'Newsletter CETROBON - Nouveautés de la semaine',
        content: `
          <h1>Bonjour {{name}},</h1>
          <p>Découvrez les nouveaux ateliers culinaires disponibles cette semaine :</p>
          
          <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 8px;">
            <h3>🍳 Atelier Pâtisserie Française</h3>
            <p>Apprenez à réaliser des macarons parfaits avec Chef Marie</p>
            <p><strong>Date :</strong> Samedi 20 octobre</p>
            <p><strong>Prix :</strong> 75€</p>
          </div>
          
          <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 8px;">
            <h3>🥘 Cuisine Méditerranéenne</h3>
            <p>Découvrez les saveurs de la Méditerranée avec Chef Antoine</p>
            <p><strong>Date :</strong> Dimanche 21 octobre</p>
            <p><strong>Prix :</strong> 85€</p>
          </div>
          
          <p>Réservez dès maintenant sur <a href="https://cetrobon.com">CETROBON.com</a></p>
          
          <p>À bientôt,<br>L'équipe CETROBON</p>
        `,
        type: 'email',
        created_at: new Date('2024-09-01')
      },
      {
        id: 'template-2',
        name: 'Promotion Atelier',
        subject: '🎉 Offre spéciale - 20% de réduction sur votre prochain atelier',
        content: `
          <h1>Offre exclusive pour {{name}} !</h1>
          
          <div style="text-align: center; margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border-radius: 12px;">
            <h2 style="color: white; margin: 0;">🎉 -20% SUR TOUS LES ATELIERS</h2>
            <p style="color: white; font-size: 18px; margin: 10px 0;">Code : PROMO20</p>
            <p style="color: white; margin: 0;">Valable jusqu'au 31 octobre 2024</p>
          </div>
          
          <p>Profitez de cette offre exceptionnelle pour découvrir nos ateliers culinaires :</p>
          
          <ul>
            <li>🍰 Pâtisserie et desserts</li>
            <li>🍝 Cuisine italienne authentique</li>
            <li>🍣 Sushi et cuisine japonaise</li>
            <li>🥖 Boulangerie traditionnelle</li>
          </ul>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="https://cetrobon.com/ateliers" style="background-color: #ff6b35; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold;">Réserver maintenant</a>
          </div>
          
          <p><small>Cette offre ne peut être cumulée avec d'autres promotions.</small></p>
        `,
        type: 'email',
        created_at: new Date('2024-09-10')
      }
    ];

    setCampaigns(demoCampaigns);
    setSubscribers(demoSubscribers);
    setTemplates(demoTemplates);
    setStats({
      totalSubscribers: demoSubscribers.length,
      totalCampaigns: demoCampaigns.length,
      totalSent: 195,
      avgOpenRate: 59.3,
      avgClickRate: 15.3
    });
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    
    if (!campaignForm.name || !campaignForm.subject) {
      showAlertMessage('Erreur', 'Le nom et le sujet sont requis', 'error');
      return;
    }

    try {
      setLoading(true);
      
      const campaignId = `campaign-${Date.now()}`;
      const campaignData = {
        ...campaignForm,
        id: campaignId,
        createdAt: new Date(),
        createdBy: user?.email || 'unknown',
        sentCount: 0,
        openCount: 0,
        clickCount: 0
      };

      const campaignRef = doc(db, 'campaigns', campaignId);
      await setDoc(campaignRef, campaignData);

      setCampaigns(prev => [...prev, campaignData]);
      setShowCreateCampaign(false);
      setCampaignForm({
        name: '',
        subject: '',
        content: '',
        type: 'email',
        template: '',
        scheduledDate: '',
        targetAudience: 'all',
        status: 'draft'
      });

      showAlertMessage('Succès', 'Campagne créée avec succès !', 'success');
      console.log('✅ Campagne créée dans Firestore:', campaignId);
    } catch (error) {
      console.error('Erreur lors de la création de campagne:', error);
      showAlertMessage('Erreur', 'Erreur lors de la création de la campagne', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      const campaign = campaigns.find(c => c.id === campaignId);
      if (!campaign) return;

      // Simuler l'envoi
      const targetCount = getTargetAudienceCount(campaign.targetAudience);
      
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        status: 'sent',
        sentAt: new Date(),
        sentCount: targetCount
      });

      // Mettre à jour localement
      setCampaigns(prev => prev.map(c => 
        c.id === campaignId 
          ? { ...c, status: 'sent', sentAt: new Date(), sentCount: targetCount }
          : c
      ));

      showAlertMessage('Succès', `Campagne envoyée à ${targetCount} destinataires !`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      showAlertMessage('Erreur', 'Erreur lors de l\'envoi de la campagne', 'error');
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    
    if (!subscriberForm.email) {
      showAlertMessage('Erreur', 'L\'email est requis', 'error');
      return;
    }

    try {
      setLoading(true);
      
      const subscriberId = `subscriber-${Date.now()}`;
      const subscriberData = {
        ...subscriberForm,
        id: subscriberId,
        subscribedAt: new Date(),
        isActive: true
      };

      const subscriberRef = doc(db, 'newsletter-subscribers', subscriberId);
      await setDoc(subscriberRef, subscriberData);

      setSubscribers(prev => [...prev, subscriberData]);
      setShowAddSubscriber(false);
      setSubscriberForm({
        email: '',
        name: '',
        phone: '',
        type: 'client',
        newsletter: true,
        sms: false
      });

      showAlertMessage('Succès', 'Abonné ajouté avec succès !', 'success');
      console.log('✅ Abonné ajouté dans Firestore:', subscriberId);
    } catch (error) {
      console.error('Erreur lors de l\'ajout d\'abonné:', error);
      showAlertMessage('Erreur', 'Erreur lors de l\'ajout de l\'abonné', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTargetAudienceCount = (audience) => {
    switch (audience) {
      case 'all': return subscribers.length;
      case 'chefs': return subscribers.filter(s => s.type === 'chef').length;
      case 'clients': return subscribers.filter(s => s.type === 'client').length;
      default: return subscribers.length;
    }
  };

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowPreviewTemplate(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject || '',
      content: template.content || '',
      type: template.type || 'email'
    });
    setShowEditTemplate(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    
    if (!templateForm.name) {
      showAlertMessage('Erreur', 'Le nom du template est requis', 'error');
      return;
    }

    try {
      setLoading(true);
      
      if (selectedTemplate) {
        // Modifier template existant
        const templateRef = doc(db, 'email-templates', selectedTemplate.id);
        await updateDoc(templateRef, {
          ...templateForm,
          updated_at: new Date()
        });

        // Mettre à jour localement
        setTemplates(prev => prev.map(t => 
          t.id === selectedTemplate.id ? { ...t, ...templateForm } : t
        ));

        showAlertMessage('Succès', 'Template modifié avec succès !', 'success');
      } else {
        // Créer nouveau template
        const templateId = `template-${Date.now()}`;
        const templateData = {
          ...templateForm,
          id: templateId,
          created_at: new Date(),
          created_by: user?.email || 'unknown'
        };

        const templateRef = doc(db, 'email-templates', templateId);
        await setDoc(templateRef, templateData);

        setTemplates(prev => [...prev, templateData]);
        showAlertMessage('Succès', 'Template créé avec succès !', 'success');
      }

      setShowCreateTemplate(false);
      setShowEditTemplate(false);
      setSelectedTemplate(null);
      setTemplateForm({
        name: '',
        subject: '',
        content: '',
        type: 'email'
      });

    } catch (error) {
      console.error('Erreur lors de la sauvegarde du template:', error);
      showAlertMessage('Erreur', 'Erreur lors de la sauvegarde du template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'secondary',
      scheduled: 'default',
      sent: 'default',
      failed: 'destructive'
    };
    
    const labels = {
      draft: 'Brouillon',
      scheduled: 'Programmé',
      sent: 'Envoyé',
      failed: 'Échec'
    };

    return <Badge variant={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const showAlertMessage = (title, message, type) => {
    setAlertData({ title, message, type });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Chargement du gestionnaire de campagnes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestionnaire de Campagnes</h1>
            <p className="text-gray-600">Gérez vos newsletters et campagnes SMS</p>
          </div>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Fermer
          </Button>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Abonnés</p>
                  <p className="text-2xl font-bold">{stats.totalSubscribers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Mail className="w-8 h-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Campagnes</p>
                  <p className="text-2xl font-bold">{stats.totalCampaigns}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Send className="w-8 h-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Envoyés</p>
                  <p className="text-2xl font-bold">{stats.totalSent}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Eye className="w-8 h-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Taux ouverture</p>
                  <p className="text-2xl font-bold">{stats.avgOpenRate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <BarChart3 className="w-8 h-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Taux de clic</p>
                  <p className="text-2xl font-bold">{stats.avgClickRate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="campaigns">
              <Mail className="w-4 h-4 mr-2" />
              Campagnes
            </TabsTrigger>
            <TabsTrigger value="subscribers">
              <Users className="w-4 h-4 mr-2" />
              Abonnés
            </TabsTrigger>
            <TabsTrigger value="templates">
              <Edit className="w-4 h-4 mr-2" />
              Templates
            </TabsTrigger>
          </TabsList>

          {/* Onglet Campagnes */}
          <TabsContent value="campaigns" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Mes Campagnes</h2>
              <Button onClick={() => setShowCreateCampaign(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Campagne
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {campaigns.map(campaign => (
                <Card key={campaign.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">{campaign.name}</h3>
                        <p className="text-gray-600">{campaign.subject}</p>
                        <div className="flex items-center space-x-4 mt-2">
                          {getStatusBadge(campaign.status)}
                          <Badge variant="outline">
                            {campaign.type === 'email' ? 'Email' : 'SMS'}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            {campaign.sentCount || 0} envoyés
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {campaign.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => handleSendCampaign(campaign.id)}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Envoyer
                          </Button>
                        )}
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {campaign.status === 'sent' && (
                      <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{campaign.openCount || 0}</p>
                          <p className="text-sm text-gray-600">Ouvertures</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{campaign.clickCount || 0}</p>
                          <p className="text-sm text-gray-600">Clics</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-orange-600">
                            {campaign.sentCount > 0 ? ((campaign.openCount || 0) / campaign.sentCount * 100).toFixed(1) : 0}%
                          </p>
                          <p className="text-sm text-gray-600">Taux ouverture</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Onglet Abonnés */}
          <TabsContent value="subscribers" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Abonnés à la Newsletter</h2>
              <div className="flex space-x-2">
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Exporter
                </Button>
                <Button onClick={() => setShowAddSubscriber(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter Abonné
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {subscribers.map(subscriber => (
                        <tr key={subscriber.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {subscriber.email || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {subscriber.name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={subscriber.type === 'chef' ? 'default' : 'secondary'}>
                              {subscriber.type}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={subscriber.isActive ? 'default' : 'secondary'}>
                              {subscriber.isActive ? 'Actif' : 'Inactif'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {subscriber.subscribedAt ? new Date(subscriber.subscribedAt.seconds * 1000).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex space-x-2">
                              <Button variant="outline" size="sm">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Templates */}
          <TabsContent value="templates" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Templates Email</h2>
              <Button onClick={() => setShowCreateTemplate(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Template
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map(template => (
                <Card key={template.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold">{template.name}</h3>
                      <Badge variant="outline">{template.type}</Badge>
                    </div>
                    <p className="text-gray-600 mb-2">{template.subject || 'Pas de sujet'}</p>
                    <p className="text-sm text-gray-500 mb-4">
                      Créé le {template.created_at ? 
                        (template.created_at.toDate ? template.created_at.toDate().toLocaleDateString('fr-FR') : new Date(template.created_at).toLocaleDateString('fr-FR')) 
                        : 'Date inconnue'}
                    </p>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm"
                        onClick={() => handlePreviewTemplate(template)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Aperçu
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={async () => {
                          try {
                            await deleteDoc(doc(db, 'email-templates', template.id));
                            setTemplates(prev => prev.filter(t => t.id !== template.id));
                            showAlertMessage('Succès', 'Template supprimé avec succès !', 'success');
                          } catch (error) {
                            showAlertMessage('Erreur', 'Erreur lors de la suppression', 'error');
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {templates.length === 0 && (
                <div className="col-span-3 text-center py-12">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Aucun template</h3>
                  <p className="text-gray-500 mb-4">Créez votre premier template d'email</p>
                  <Button onClick={() => setShowCreateTemplate(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un template
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Nouvelle Campagne */}
      {showCreateCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Nouvelle Campagne</h3>
              <Button variant="outline" size="sm" onClick={() => setShowCreateCampaign(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <form onSubmit={handleCreateCampaign} className="space-y-4">
              <div>
                <Label htmlFor="campaignName">Nom de la campagne</Label>
                <Input
                  id="campaignName"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm(prev => ({...prev, name: e.target.value}))}
                  placeholder="Ex: Newsletter Novembre 2024"
                />
              </div>
              
              <div>
                <Label htmlFor="campaignType">Type</Label>
                <select
                  id="campaignType"
                  value={campaignForm.type}
                  onChange={(e) => setCampaignForm(prev => ({...prev, type: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="campaignSubject">Sujet</Label>
                <Input
                  id="campaignSubject"
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm(prev => ({...prev, subject: e.target.value}))}
                  placeholder="Ex: Découvrez nos nouveaux ateliers !"
                />
              </div>
              
              <div>
                <Label htmlFor="campaignContent">Contenu</Label>
                <textarea
                  id="campaignContent"
                  value={campaignForm.content}
                  onChange={(e) => setCampaignForm(prev => ({...prev, content: e.target.value}))}
                  placeholder="Rédigez votre message ici..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
                />
              </div>
              
              <div>
                <Label htmlFor="targetAudience">Audience cible</Label>
                <select
                  id="targetAudience"
                  value={campaignForm.targetAudience}
                  onChange={(e) => setCampaignForm(prev => ({...prev, targetAudience: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="all">Tous les abonnés</option>
                  <option value="chefs">Chefs uniquement</option>
                  <option value="clients">Clients uniquement</option>
                </select>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button type="submit" disabled={loading}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer la campagne
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateCampaign(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ajouter Abonné */}
      {showAddSubscriber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Ajouter un Abonné</h3>
              <Button variant="outline" size="sm" onClick={() => setShowAddSubscriber(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <form onSubmit={handleAddSubscriber} className="space-y-4">
              <div>
                <Label htmlFor="subscriberEmail">Email *</Label>
                <Input
                  id="subscriberEmail"
                  type="email"
                  value={subscriberForm.email}
                  onChange={(e) => setSubscriberForm(prev => ({...prev, email: e.target.value}))}
                  placeholder="email@exemple.com"
                />
              </div>
              
              <div>
                <Label htmlFor="subscriberName">Nom</Label>
                <Input
                  id="subscriberName"
                  value={subscriberForm.name}
                  onChange={(e) => setSubscriberForm(prev => ({...prev, name: e.target.value}))}
                  placeholder="Nom complet"
                />
              </div>
              
              <div>
                <Label htmlFor="subscriberType">Type</Label>
                <select
                  id="subscriberType"
                  value={subscriberForm.type}
                  onChange={(e) => setSubscriberForm(prev => ({...prev, type: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="client">Client</option>
                  <option value="chef">Chef</option>
                </select>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button type="submit" disabled={loading}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddSubscriber(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Aperçu Template */}
      {showPreviewTemplate && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Aperçu : {selectedTemplate.name}</h3>
              <Button variant="outline" size="sm" onClick={() => setShowPreviewTemplate(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="border rounded-lg p-4 mb-4">
              <div className="border-b pb-2 mb-4">
                <p className="text-sm text-gray-600">Sujet :</p>
                <p className="font-medium">{selectedTemplate.subject}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: (selectedTemplate.content || 'Contenu non disponible')
                      .replace(/{{name}}/g, 'John Doe')
                      .replace(/{{email}}/g, 'john.doe@example.com')
                      .replace(/{{unsubscribe_link}}/g, '#unsubscribe')
                  }}
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <Button 
                onClick={() => {
                  setShowPreviewTemplate(false);
                  handleEditTemplate(selectedTemplate);
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifier ce template
              </Button>
              <Button variant="outline" onClick={() => setShowPreviewTemplate(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier/Créer Template */}
      {(showCreateTemplate || showEditTemplate) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">
                {showEditTemplate ? `Modifier : ${selectedTemplate?.name}` : 'Nouveau Template'}
              </h3>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setShowCreateTemplate(false);
                  setShowEditTemplate(false);
                  setSelectedTemplate(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <form onSubmit={handleSaveTemplate} className="space-y-4">
              <div>
                <Label htmlFor="templateName">Nom du template</Label>
                <Input
                  id="templateName"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({...prev, name: e.target.value}))}
                  placeholder="Ex: Newsletter hebdomadaire"
                />
              </div>
              
              <div>
                <Label htmlFor="templateType">Type</Label>
                <select
                  id="templateType"
                  value={templateForm.type}
                  onChange={(e) => setTemplateForm(prev => ({...prev, type: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              
              {templateForm.type === 'email' && (
                <>
                  <div>
                    <Label htmlFor="templateSubject">Sujet de l'email</Label>
                    <Input
                      id="templateSubject"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm(prev => ({...prev, subject: e.target.value}))}
                      placeholder="Ex: Newsletter CETROBON - Nouveautés"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="templateContent">Contenu HTML</Label>
                    <textarea
                      id="templateContent"
                      value={templateForm.content}
                      onChange={(e) => setTemplateForm(prev => ({...prev, content: e.target.value}))}
                      placeholder="Rédigez votre template HTML ici..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md h-64 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Variables disponibles : {'{{name}}, {{email}}, {{unsubscribe_link}}'}
                    </p>
                  </div>
                </>
              )}
              
              {templateForm.type === 'sms' && (
                <div>
                  <Label htmlFor="templateContent">Message SMS</Label>
                  <textarea
                    id="templateContent"
                    value={templateForm.content}
                    onChange={(e) => setTemplateForm(prev => ({...prev, content: e.target.value}))}
                    placeholder="Rédigez votre message SMS ici..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md h-24"
                    maxLength="160"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {templateForm.content.length}/160 caractères
                  </p>
                </div>
              )}
              
              <div className="flex space-x-3 pt-4">
                <Button type="submit" disabled={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  {showEditTemplate ? 'Modifier' : 'Créer'} le template
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateTemplate(false);
                    setShowEditTemplate(false);
                    setSelectedTemplate(null);
                  }}
                >
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              {alertData.type === 'success' ? (
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              )}
              <h3 className="text-lg font-semibold">{alertData.title}</h3>
            </div>
            <p className="text-gray-600 mb-4">{alertData.message}</p>
            <div className="flex justify-end">
              <Button onClick={() => setShowAlert(false)}>
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManager;